/**
 * SagePanel.tsx
 *
 * The Sage co-pilot panel content — header, messages, input area.
 * Receives all state and callbacks as props from the orchestrator.
 * Used inside both the desktop resizable panel and the mobile drawer.
 */
import React, { useRef, useEffect } from "react";
import { X, Send, Loader2, MessageSquare } from "lucide-react";
import { Streamdown } from "streamdown";
import {
  SAGE,
  SAGE_MUTED,
  RESPONSE_MODE_LABELS,
  FEATURE_SUGGESTED_QUESTIONS,
  FEATURE_RESPONSE_MODE,
  type ResponseMode,
} from "./config";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

export interface SagePanelProps {
  messages: Message[];
  input: string;
  setInput: (v: string) => void;
  isStreaming: boolean;
  mode: ResponseMode;
  setMode: (m: ResponseMode) => void;
  sageName: string;
  avatarUrl: string;
  activePage?: string;
  pageTitle?: string;
  pageBadge?: string;
  greeting: string;
  suggestions?: string[];
  onSend: (text: string) => void;
  onClear: () => void;
  onClose: () => void;
}

// ── Streamdown link component — opens all links in new window ─────────────────
const streamdownComponents = {
  a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:opacity-80 transition-opacity"
      style={{ color: SAGE }}
      {...props}
    >
      {children}
    </a>
  ),
};

// ── Rich block renderer ───────────────────────────────────────────────────────
function RichBlock({ raw }: { raw: string }) {
  const lines = raw.trim().split("\n");
  const type = lines[0]?.replace(":::", "").trim();
  const body: Record<string, string> = {};
  for (const line of lines.slice(1)) {
    const idx = line.indexOf(":");
    if (idx > -1) {
      const key = line.slice(0, idx).trim();
      const val = line.slice(idx + 1).trim();
      body[key] = val;
    }
  }

  if (type === "stat") {
    return (
      <div className="my-2 rounded-lg p-3 text-center" style={{ background: SAGE_MUTED, borderLeft: `3px solid ${SAGE}` }}>
        <div className="text-2xl font-bold" style={{ color: SAGE }}>{body.value}</div>
        <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{body.label}</div>
        {body.source && <div className="text-[10px] text-gray-400 mt-1">Source: {body.source}</div>}
      </div>
    );
  }

  if (type === "compare") {
    const leftItems = (body.left_items ?? "").split("|");
    const rightItems = (body.right_items ?? "").split("|");
    return (
      <div className="my-2 rounded-lg overflow-hidden border border-border">
        <div className="grid grid-cols-2">
          <div className="p-2 bg-muted">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">{body.left_title}</div>
            {leftItems.map((item, i) => (
              <div key={i} className="text-xs text-foreground py-0.5 border-b border-border/40 last:border-0">{item}</div>
            ))}
          </div>
          <div className="p-2" style={{ background: SAGE_MUTED }}>
            <div className="text-[10px] font-semibold uppercase mb-1" style={{ color: SAGE }}>{body.right_title}</div>
            {rightItems.map((item, i) => (
              <div key={i} className="text-xs py-0.5 border-b border-border/40 last:border-0 text-foreground">{item}</div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (type === "insight") {
    return (
      <div className="my-2 rounded-lg p-3" style={{ background: SAGE_MUTED, borderLeft: `3px solid ${SAGE}` }}>
        {body.title && <div className="text-xs font-semibold mb-1" style={{ color: SAGE }}>{body.title}</div>}
        <div className="text-xs text-foreground">{body.body}</div>
      </div>
    );
  }

  if (type === "steps") {
    const items = (body.items ?? "").split("|");
    return (
      <div className="my-2 rounded-lg p-3" style={{ background: SAGE_MUTED }}>
        {body.title && <div className="text-xs font-semibold mb-2" style={{ color: SAGE }}>{body.title}</div>}
        <ol className="space-y-1">
          {items.map((item, i) => (
            <li key={i} className="text-xs text-foreground flex gap-2">
              <span className="font-semibold shrink-0" style={{ color: SAGE }}>{i + 1}.</span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  if (type === "instagram") {
    const imageUrl = body.image || "";
    const date = body.date || "";
    const postUrl = body.url || "";
    const caption = body.caption || "";
    const postType = (body.type || "Image").trim();
    const likes = parseInt(body.likes || "0", 10);
    const comments = parseInt(body.comments || "0", 10);
    const isVideo = postType === "Video";
    const isSidecar = postType === "Sidecar";
    return (
      <div
        className="my-2 rounded-lg overflow-hidden border border-border"
        style={{ background: SAGE_MUTED }}
      >
        {imageUrl && (
          <div className="relative w-full" style={{ aspectRatio: "1 / 1", maxHeight: 220 }}>
            <img
              src={imageUrl}
              alt="Instagram post"
              className="w-full h-full object-cover"
              style={{ maxHeight: 220 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
            {/* Play button overlay for videos */}
            {isVideo && (
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ background: "rgba(0,0,0,0.18)" }}
              >
                <div
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(255,255,255,0.88)",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.22)",
                  }}
                >
                  {/* Triangle play icon */}
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <polygon points="6,4 15,9 6,14" fill={SAGE} />
                  </svg>
                </div>
              </div>
            )}
            {/* Sidecar (carousel) badge */}
            {isSidecar && (
              <div
                className="absolute top-2 right-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded-md"
                style={{ background: "rgba(0,0,0,0.52)" }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <rect x="1" y="3" width="7" height="7" rx="1.2" stroke="white" strokeWidth="1.2" fill="none" />
                  <rect x="4" y="1" width="7" height="7" rx="1.2" stroke="white" strokeWidth="1.2" fill="none" />
                </svg>
                <span className="text-white" style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.04em" }}>ALBUM</span>
              </div>
            )}
          </div>
        )}
        {/* Type badge when no image */}
        {!imageUrl && isVideo && (
          <div className="flex items-center gap-1.5 px-3 pt-2.5">
            <div
              className="flex items-center justify-center rounded-full"
              style={{ width: 28, height: 28, background: SAGE }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <polygon points="4,2.5 10,6 4,9.5" fill="white" />
              </svg>
            </div>
            <span className="text-[10px] font-semibold" style={{ color: SAGE }}>Video</span>
          </div>
        )}
        <div className="p-2.5">
          {caption && (
            <p className="text-xs text-foreground leading-relaxed line-clamp-3 mb-1.5">
              {caption}
            </p>
          )}
          {/* Engagement footer */}
          {(likes > 0 || comments > 0) && (
            <div className="flex items-center gap-3 mb-1">
              {likes > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  {likes.toLocaleString()}
                </span>
              )}
              {comments > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  {comments.toLocaleString()}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            {date && (
              <span className="text-[10px] text-muted-foreground">{date}</span>
            )}
            {postUrl && (
              <a
                href={postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] font-semibold flex items-center gap-0.5 hover:opacity-80 transition-opacity"
                style={{ color: SAGE }}
              >
                View on Instagram ↗
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ── Message renderer ─────────────────────────────────────────────────────────
function MessageContent({ content, role }: { content: string; role: "user" | "assistant" }) {
  if (role === "user") {
    return <div className="text-sm leading-relaxed whitespace-pre-wrap">{content}</div>;
  }
  const parts = content.split(/(:::[\s\S]*?:::)/g);
  return (
    <div className="text-sm leading-relaxed">
      {parts.map((part, i) => {
        if (part.startsWith(":::")) {
          return <RichBlock key={i} raw={part} />;
        }
        if (!part.trim()) return null;
        return (
          <div key={i} className="prose prose-sm max-w-none dark:prose-invert [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-xs [&_strong]:font-semibold">
            <Streamdown
              components={streamdownComponents as Parameters<typeof Streamdown>[0]["components"]}
            >
              {part}
            </Streamdown>
          </div>
        );
      })}
    </div>
  );
}

// ── Streaming word-reveal ────────────────────────────────────────────────────
function StreamingText({ content }: { content: string }) {
  const words = content.split(/(\s+)/g);
  return (
    <span>
      {words.map((word, i) => (
        <span
          key={i}
          style={{
            display: "inline",
            opacity: 0,
            animation: `fadeWordIn 0.18s ease-out ${Math.floor(i / 2) * 28}ms forwards`,
          }}
        >
          {word}
        </span>
      ))}
    </span>
  );
}

// ── Main Panel Component ─────────────────────────────────────────────────────
export default function SagePanel({
  messages,
  input,
  setInput,
  isStreaming,
  mode,
  setMode,
  sageName,
  avatarUrl: _avatarUrl,
  pageBadge,
  greeting,
  suggestions,
  onSend,
  onClear,
  onClose,
}: SagePanelProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Only auto-focus on desktop — on iOS, auto-focus triggers the keyboard
    // which causes layout shifts and is disruptive
    if (messages.length === 0 && window.innerWidth >= 768) {
      inputRef.current?.focus();
    }
  }, [messages.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend(input);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background border-l border-border">
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 shrink-0"
        style={{ background: SAGE }}
      >
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
          <MessageSquare className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm leading-tight">{sageName}</div>
          <div className="text-white/70 text-[11px] flex items-center gap-1.5">
            <span>Lola &amp; Ber guide</span>
            {pageBadge && (
              <>
                <span className="opacity-40">·</span>
                <span className="opacity-80 truncate max-w-[140px]">{pageBadge}</span>
              </>
            )}
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={onClear}
            className="text-white/60 hover:text-white text-[11px] px-2 py-1 rounded transition-colors"
            title="Clear conversation"
          >
            Clear
          </button>
        )}
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white transition-colors ml-1"
          aria-label="Close Sage"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3 min-h-0">
        {messages.length === 0 ? (
          <div className="py-1">
            {/* Greeting bubble */}
            <div
              className="flex gap-2 justify-start"
              style={{ animation: "fadeSlideUp 0.4s ease-out both" }}
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 border border-border" style={{ background: SAGE_MUTED }}>
                <MessageSquare className="w-4 h-4" style={{ color: SAGE }} />
              </div>
              <div className="max-w-[82%] rounded-2xl rounded-tl-sm px-3 py-2 text-sm leading-relaxed text-foreground border border-border bg-muted">
                {greeting}
              </div>
            </div>
            <div className="mt-3">
              {FEATURE_SUGGESTED_QUESTIONS && suggestions && suggestions.length > 0 && (
                <div className="space-y-2">
                  {suggestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => onSend(q)}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg border transition-colors hover:border-transparent"
                      style={{
                        background: SAGE_MUTED,
                        borderColor: "#d1e8da",
                        color: "var(--foreground, #374151)",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = "#d4ead9";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = SAGE_MUTED;
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div
              key={i}
              className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {msg.role === "assistant" && (
                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 border border-border" style={{ background: SAGE_MUTED }}>
                  <MessageSquare className="w-4 h-4" style={{ color: SAGE }} />
                </div>
              )}
              <div
                className={`max-w-[82%] rounded-2xl px-3 py-2 ${
                  msg.role === "user"
                    ? "text-white rounded-tr-sm"
                    : "text-foreground rounded-tl-sm border border-border bg-muted"
                }`}
                style={msg.role === "user" ? { background: SAGE } : {}}
              >
                {msg.role === "assistant" && !msg.content && isStreaming ? (
                  <div className="flex items-center gap-1 py-1">
                    <span
                      className="inline-block w-2 h-4 rounded-sm"
                      style={{
                        background: SAGE,
                        opacity: 0.7,
                        animation: "sageCursorBlink 0.9s ease-in-out infinite",
                      }}
                    />
                  </div>
                ) : msg.role === "assistant" && isStreaming && i === messages.length - 1 ? (
                  <div className="text-sm leading-relaxed">
                    <StreamingText content={msg.content} />
                  </div>
                ) : (
                  <MessageContent content={msg.content} role={msg.role} />
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="shrink-0 border-t border-border px-3 py-2">
        {/* Mode selector — visible 3-segment pill */}
        {FEATURE_RESPONSE_MODE && (
          <div className="flex items-center gap-1 mb-2">
            <span className="text-[10px] text-muted-foreground mr-1 shrink-0">Length:</span>
            <div className="flex rounded-lg overflow-hidden border border-border bg-muted p-0.5 gap-0.5">
              {(["concise", "standard", "detailed"] as ResponseMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className="px-2.5 py-0.5 text-[11px] font-medium rounded-md transition-all duration-150"
                  style={mode === m
                    ? { background: SAGE, color: "white" }
                    : { background: "transparent", color: "var(--muted-foreground)" }
                  }
                >
                  {RESPONSE_MODE_LABELS[m]}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              // iOS Safari fallback: auto-resize textarea since fieldSizing is not supported
              const el = e.target;
              el.style.height = "auto";
              el.style.height = Math.min(el.scrollHeight, 120) + "px";
            }}
            onKeyDown={handleKeyDown}
            placeholder="Ask Sage anything…"
            rows={1}
            disabled={isStreaming}
            className="flex-1 resize-none text-base md:text-sm border border-border rounded-xl px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:border-transparent disabled:opacity-50 ios-autoresize"
            style={{
              maxHeight: "120px",
              overflowY: "auto",
              fieldSizing: "content",
              "--tw-ring-color": SAGE,
            } as React.CSSProperties}
          />
          <button
            onClick={() => onSend(input)}
            disabled={!input.trim() || isStreaming}
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 active:scale-95"
            style={{ background: SAGE }}
            aria-label="Send"
          >
            {isStreaming ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Send className="w-4 h-4 text-white" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


